import React from "react";
import "./InvoiceDetails.css";
import generatePrintContent from "./InvoicePrintTemplate";

const InvoiceDetailsModal = ({ invoice, onClose }) => {
    if (!invoice) return null;

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const formatCurrency = (amount) => {
        return `Rs.${parseFloat(amount || 0).toFixed(2)}`;
    };

    const handlePrint = () => {
        const printWindow = window.open("", "_blank");
        const invoiceData = {
            invoiceTitle: 'Sales Invoice',
            invoiceNo: invoice.invoice_number,
            date: invoice.invoice_date,
            salesRepresentative: invoice.sales_representative,
            paymentMethod: invoice.payment_method,
            paymentStatus: invoice.payment_status,
            customerNic: invoice.customer_id,
            customerName: `${invoice.FirstName} ${invoice.LastName}`,
            customerPhone: invoice.ContactNumber || "No Phone Provided",
            customerAddress: invoice.Address,
            items: invoice.items.map(p => ({
                name: p.product_name,
                quantity: p.quantity,
                unitPrice: p.unit_price,
                totalPrice: p.total_price,
            })),
            total: invoice.total_amount,
            discount: invoice.discount,
            grandTotal: invoice.final_amount || invoice.total_amount,
            notes: invoice.notes,
            serialNumber: invoice.serial_number,
        };

        const printContent = generatePrintContent(invoiceData);
        printWindow.document.write(printContent);
        printWindow.document.close();

        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const orderItems = invoice.items || [];

    return (
        <div className="idm-overlay" onClick={onClose}>
            <div className="idm-content" onClick={(e) => e.stopPropagation()}>
                <div className="idm-header">
                    <h3 className="idm-title">Invoice #{invoice.invoice_number}</h3>
                    <button className="idm-close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="idm-info-grid">
                    <div className="idm-info-section">
                        <h4>Customer Information</h4>
                        <div className="idm-info-row">
                            <span className="idm-info-label">Name:</span>
                            <span className="idm-info-value">{invoice.FirstName} {invoice.LastName}</span>
                        </div>
                        <div className="idm-info-row">
                            <span className="idm-info-label">ID:</span>
                            <span className="idm-info-value">{invoice.customer_id}</span>
                        </div>
                        <div className="idm-info-row">
                            <span className="idm-info-label">Phone:</span>
                            <span className="idm-info-value">{invoice.ContactNumber || 'N/A'}</span>
                        </div>
                        {invoice.Address && (
                            <div className="idm-info-row">
                                <span className="idm-info-label">Address:</span>
                                <span className="idm-info-value">{invoice.Address}</span>
                            </div>
                        )}
                    </div>

                    <div className="idm-info-section">
                        <h4>Invoice Details</h4>
                        <div className="idm-info-row">
                            <span className="idm-info-label">Invoice Date:</span>
                            <span className="idm-info-value">{formatDate(invoice.invoice_date)}</span>
                        </div>
                        <div className="idm-info-row">
                            <span className="idm-info-label">Payment Status:</span>
                            <span className={`idm-status ${invoice.payment_status === "Completed" ? "idm-status-completed" : "idm-status-pending"}`}>
                                {invoice.payment_status}
                            </span>
                        </div>
                        <div className="idm-info-row">
                            <span className="idm-info-label">Payment Method:</span>
                            <span className="idm-info-value">{invoice.payment_method || 'N/A'}</span>
                        </div>
                        {invoice.sales_representative && (
                            <div className="idm-info-row">
                                <span className="idm-info-label">Sales Rep:</span>
                                <span className="idm-info-value">{invoice.sales_representative}</span>
                            </div>
                        )}
                        {invoice.invoice_type && (
                            <div className="idm-info-row">
                                <span className="idm-info-label">Invoice Type:</span>
                                <span className="idm-info-value" style={{ textTransform: 'capitalize' }}>{invoice.invoice_type}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="idm-section-title">Order Items</div>
                <div className="idm-table-container">
                    {orderItems.length > 0 ? (
                        <table className="idm-items-table">
                            <thead>
                            <tr>
                                <th>Item</th>
                                <th>Category</th>
                                <th>Quantity</th>
                                <th>Unit Price</th>
                                <th>Total Price</th>
                            </tr>
                            </thead>
                            <tbody>
                            {orderItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.product_name || `Product #${item.product_id}`}</td>
                                    <td>{item.category_name || 'N/A'}</td>
                                    <td>{item.quantity}</td>
                                    <td>{formatCurrency(item.unit_price)}</td>
                                    <td>{formatCurrency(item.total_price)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="idm-no-items">
                            No items found for this invoice.
                        </div>
                    )}
                </div>

                <div className="idm-totals">
                    <div className="idm-totals-row">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(invoice.total_amount)}</span>
                    </div>
                    <div className="idm-totals-row">
                        <span>Discount:</span>
                        <span>{formatCurrency(invoice.discount)}</span>
                    </div>
                    <div className="idm-divider"></div>
                    <div className="idm-totals-row idm-total">
                        <span>Total:</span>
                        <span>{formatCurrency(invoice.final_amount || invoice.total_amount)}</span>
                    </div>
                </div>

                <div className="idm-info-box">
                    <h4>Serial Number</h4>
                    <p>{invoice.serial_number || 'No serial number for this invoice.'}</p>
                </div>

                <div className="idm-info-box">
                    <h4>Notes</h4>
                    <p>{invoice.notes || 'No additional notes for this invoice.'}</p>
                </div>

                <button className="idm-print-btn" onClick={handlePrint}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    Print Invoice
                </button>
            </div>
        </div>
    );
};

export default InvoiceDetailsModal;